import { Table, Checkbox } from "antd";
import { useMemo } from "react";

import NoticesTable from "@/components/tables/NoticesTable";

import { createColumns } from "./tableConfig.js";

const NoticesGroupsTable = ({
  data,
  loading,
  pagination,
  onChange,
  selectedRowKeys = [],
  onRowsSelect,
  onReadNotice,
  onUnreadNotice,
  scroll = { x: "100%" },
}) => {
  // Мемоизированные колонки таблицы
  const columns = useMemo(() => createColumns(onReadNotice), [onReadNotice]);

  const uuidsWithChildren = useMemo(() => {
    return data.reduce((acc, cur) => {
      return [...acc, cur.uuid, ...cur.events.map(({ uuid }) => uuid)];
    }, []);
  }, [data]);

  // Функция для получения всех дочерних ключей
  const getChildrenKeys = (record) => {
    if (!record.events) return [];
    return record.events.map((child) => child.uuid);
  };

  // Проверка состояния чекбокса родителя (checked/indeterminate/unchecked)
  const getParentCheckState = (record) => {
    const childrenKeys = getChildrenKeys(record);
    if (childrenKeys.length === 0) return "unchecked";

    const selectedChildren = childrenKeys.filter((key) =>
      selectedRowKeys.includes(key)
    );

    if (selectedChildren.length === 0) return "unchecked";
    if (selectedChildren.length === childrenKeys.length) return "checked";
    return "indeterminate";
  };

  const handleRowSelect = (group, record, selected, selectedRows) => {
    const childrenKeys = getChildrenKeys(group);

    if (selected) {
      const selectedChildren = childrenKeys.filter((key) =>
        [...selectedRowKeys, record.uuid].includes(key)
      );

      // Если выбраны все дети - добавляем родителя
      if (selectedChildren.length === childrenKeys.length) {
        onRowsSelect((prev) => [...prev, record.uuid, group.uuid]);
      } else {
        // Иначе удаляем родителя
        onRowsSelect((prev) => [
          ...prev.filter((key) => key !== group.uuid),
          record.uuid,
        ]);
      }
    } else {
      onRowsSelect((prev) =>
        prev.filter((key) => key !== record.uuid && key !== group.uuid)
      );
    }
  };

  return (
    <Table
      dataSource={data}
      columns={columns}
      rowKey="uuid"
      loading={loading}
      pagination={pagination}
      onChange={onChange}
      bordered
      showSorterTooltip={{
        target: "sorter-icon",
        title: "Нажмите для сортировки",
      }}
      scroll={scroll}
      rowClassName="top-align"
      rowSelection={{
        type: "checkbox",
        selectedRowKeys,
        preserveSelectedRowKeys: true,
        onSelect: (record, selected, selectedRows) => {
          if (selected && record.events) {
            onRowsSelect((prev) =>
              Array.from(
                new Set([
                  ...prev,
                  record.uuid,
                  ...record.events.map((child) => child.uuid),
                ])
              )
            );
          } else if (!selected && record.events) {
            onRowsSelect((prev) =>
              prev.filter(
                (key) =>
                  key !== record.uuid &&
                  !record.events.map((child) => child.uuid).includes(key)
              )
            );
          }
        },
        onSelectAll: (selected, selectedRows, changeRows) => {
          // Выбираем все строки включая детей
          const allKeys = [];
          changeRows.forEach((record) => {
            allKeys.push(record.uuid);
            if (record.events) {
              record.events.forEach((child) => allKeys.push(child.uuid));
            }
          });

          if (selected) {
            onRowsSelect((prev) => Array.from(new Set([...prev, ...allKeys])));
          } else {
            onRowsSelect((prev) =>
              prev.filter((key) => !allKeys.includes(key))
            );
          }
        },
        getCheckboxProps: (record) => {
          const checkState = getParentCheckState(record);
          return {
            indeterminate: checkState === "indeterminate",
          };
        },
        columnTitle: () => {
          return (
            <Checkbox
              indeterminate={
                selectedRowKeys.some((key) =>
                  uuidsWithChildren.includes(key)
                ) &&
                !uuidsWithChildren.every((key) => selectedRowKeys.includes(key))
              }
              checked={uuidsWithChildren.every((key) =>
                selectedRowKeys.includes(key)
              )}
              onChange={(e) =>
                onRowsSelect((prev) =>
                  e.target.checked
                    ? [...prev, ...uuidsWithChildren]
                    : [
                        ...prev.filter(
                          (key) => !uuidsWithChildren.includes(key)
                        ),
                      ]
                )
              }
            />
          );
        },
      }}
      expandable={{
        expandedRowRender: (group) => (
          <NoticesTable
            data={group.events}
            pagination={false}
            selectedRowKeys={selectedRowKeys}
            onRowsSelect={(record, selected, selectedRows) =>
              handleRowSelect(group, record, selected, selectedRows)
            }
            onReadNotice={onReadNotice}
            onUnreadNotice={onUnreadNotice}
          />
        ),
      }}
    />
  );
};

export default NoticesGroupsTable;
